import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ArrowRightIcon, CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { subscriptionApi } from '../../services/subscription.service';
import { SubscriptionPlan, SubscriptionProrationPreview } from '../../types/subscription';

type SubscriptionStatusResponse = {
  hasActiveSubscription: boolean;
  planName?: string;
  expiresAt?: string;
};

const planOrder = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'];
const PENDING_TEACHER_PAYMENT_KEY = 'pendingTeacherPlanPayment';

const resolveObjectId = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.$oid) return String(value.$oid);
  return String(value);
};

const resolvePlanId = (plan: any): string => String(plan?.id || resolveObjectId(plan?._id) || '').trim();

const featureList = (plan: SubscriptionPlan): string[] => {
  const f = plan.features || ({} as any);
  const items: string[] = [
    `Max students: ${f.maxStudents ?? 0}`,
    `Max classes: ${f.maxClasses ?? 0}`,
    `Max assignments: ${f.maxAssignments ?? 0}`,
    f.maxExamAttempts !== undefined ? `Exam attempts: ${f.maxExamAttempts}` : 'Exam attempts: N/A',
    `Analytics: ${f.hasAnalytics ? 'Enabled' : 'Not included'}`,
    `Reporting: ${f.hasAdvancedReporting ? 'Advanced' : 'Basic'}`,
    `Results access: ${f.examResultsAccess ? 'Detailed' : 'Basic'}`,
    `Support: ${String(f.supportLevel || 'email').toUpperCase()}`,
  ];

  if (f.advancedGrading) items.push('Advanced grading');
  if (f.customBranding) items.push('Custom branding');
  if (f.canScheduleClasses) items.push('Class scheduling');
  if (f.canUploadVideos) items.push('Video upload');
  if (f.canCreateQuizzes) items.push('Quiz creation');
  if (f.apiAccess) items.push('API access');
  if (f.storageLimit) items.push(`Storage: ${f.storageLimit} GB`);
  return items;
};

const currencySymbols: Record<string, string> = {
  INR: 'Rs.',
  USD: '$',
};

const accentMap: Record<string, string> = {
  FREE: 'from-slate-200 via-slate-100 to-white',
  BASIC: 'from-sky-200 via-cyan-100 to-white',
  PREMIUM: 'from-violet-200 via-fuchsia-100 to-white',
  ENTERPRISE: 'from-emerald-200 via-teal-100 to-white',
};

const TeacherSubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [status, setStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [selectedProration, setSelectedProration] = useState<SubscriptionProrationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        const [plansResponse, statusResponse, subscriptionResponse] = await Promise.allSettled([
          api.get<SubscriptionPlan[]>('/teachers/subscription/plans'),
          api.get<SubscriptionStatusResponse>('/teachers/subscription/status'),
          api.get<any>('/teachers/subscription'),
        ]);

        if (plansResponse.status !== 'fulfilled') {
          throw plansResponse.reason;
        }

        const fetchedPlansRaw = Array.isArray(plansResponse.value.data) ? plansResponse.value.data : [];
        const fetchedPlans = fetchedPlansRaw.map((plan: any) => ({
          ...plan,
          id: resolvePlanId(plan),
        }));
        setPlans(fetchedPlans);

        setStatus(statusResponse.status === 'fulfilled' ? statusResponse.value.data || null : null);

        if (subscriptionResponse.status === 'fulfilled') {
          const sub = subscriptionResponse.value.data || {};
          const resolvedCurrentPlanId = String(sub.planId || '').trim() || resolvePlanId(sub.plan) || null;
          setCurrentPlanId(resolvedCurrentPlanId);
        } else {
          setCurrentPlanId(null);
        }

        setError(null);
        setLastUpdatedAt(new Date());
      } catch (err) {
        console.error('Failed to load teacher plans:', err);
        setError('Failed to load teacher subscription plans.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
  }, []);

  useEffect(() => {
    void load(false);
    const intervalId = window.setInterval(() => {
      void load(true);
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [load]);

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => {
      const sortDelta = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
      if (sortDelta !== 0) return sortDelta;
      return planOrder.indexOf(a.planType) - planOrder.indexOf(b.planType);
    }),
    [plans],
  );
  const hasExistingSubscription = Boolean(status?.hasActiveSubscription || currentPlanId);

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    const isCurrentById = currentPlanId && currentPlanId === plan.id;
    const isCurrentByName =
      !!status?.hasActiveSubscription &&
      !!status.planName &&
      status.planName.trim().toLowerCase() === plan.name.trim().toLowerCase();
    if (isCurrentById || isCurrentByName) return;
    setSelectedPlan(plan);
    setSelectedProration(null);

    if (!hasExistingSubscription) return;

    const selectedPlanId = resolvePlanId(plan);
    if (!selectedPlanId) return;

    try {
      setPreviewLoading(true);
      const preview = await subscriptionApi.previewPlanSwitch(selectedPlanId);
      setSelectedProration(preview);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to calculate prorated switch amount.';
      setError(message);
      setSelectedPlan(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPayment = (mode: 'wallet' | 'razorpay') => {
    if (!selectedPlan || previewLoading) return;
    const selectedPlanId = resolvePlanId(selectedPlan);
    if (!selectedPlanId) {
      setError('Selected plan id is missing. Please refresh and try again.');
      return;
    }

    const paymentState = {
      planId: selectedPlanId,
      planName: selectedPlan.name,
      amount: selectedProration ? Number(selectedProration.amountDue || 0) : Number(selectedPlan.price || 0),
      originalAmount: Number(selectedPlan.price || 0),
      currency: selectedPlan.currency || 'INR',
      billingCycle: selectedPlan.billingCycle || 'monthly',
      mode: hasExistingSubscription ? 'upgrade' : 'new',
      preferredPayment: mode,
      prorationPreview: selectedProration || undefined,
    };
    localStorage.setItem(PENDING_TEACHER_PAYMENT_KEY, JSON.stringify(paymentState));

    navigate('/teacher/payments', {
      state: paymentState,
    });
  };

  const selectedAmountDue = selectedPlan
    ? selectedProration
      ? Number(selectedProration.amountDue || 0)
      : Number(selectedPlan.price || 0)
    : 0;

  if (loading) {
    return <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 text-sm text-slate-600">Loading teacher subscription plans...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Teacher plans</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Live plan selection</h2>
            <p className="mt-1 text-sm text-slate-500">
              Plans are loaded from the backend and refreshed automatically.
              {lastUpdatedAt ? ` Last updated ${lastUpdatedAt.toLocaleTimeString()}.` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load(true)}
            disabled={refreshing}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {status?.hasActiveSubscription && (
        <div className="rounded-[1.75rem] border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
          <p className="font-semibold">Active plan: {status.planName || 'Current Plan'}</p>
          {status.expiresAt ? <p className="mt-1">Renews on {new Date(status.expiresAt).toLocaleDateString()}</p> : null}
        </div>
      )}

      {error && (
        <div className="rounded-[1.75rem] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      )}

      {selectedPlan && (
        <div className="rounded-[2rem] border border-sky-200 bg-[linear-gradient(135deg,_#f8fbff_0%,_#eef6ff_100%)] p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.24)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Payment section</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{selectedPlan.name}</h2>
              <p className="mt-2 text-sm text-slate-600">
                Complete subscription for {selectedPlan.name} at {(currencySymbols[selectedPlan.currency || 'INR'] || selectedPlan.currency || 'INR')} {Number(selectedPlan.price || 0).toFixed(2)} per {selectedPlan.billingCycle}.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedPlan(null);
                setSelectedProration(null);
              }}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Clear selection
            </button>
          </div>
          <div className="mt-4 rounded-[1.25rem] border border-sky-100 bg-white/80 px-4 py-3 text-sm text-slate-700">
            {previewLoading ? (
              <span className="font-semibold text-slate-900">Calculating prorated amount...</span>
            ) : selectedProration ? (
              <span>
                Amount due now is <strong>{selectedProration.currency} {selectedAmountDue.toFixed(2)}</strong> after applying {selectedProration.currency} {Number(selectedProration.currentPlanUnusedCredit || 0).toFixed(2)} credit for {selectedProration.remainingDays} remaining days.
              </span>
            ) : (
              <span>
                Amount due now is <strong>{selectedPlan.currency || 'INR'} {selectedAmountDue.toFixed(2)}</strong>.
              </span>
            )}
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => openPayment('wallet')}
              disabled={previewLoading}
              className="rounded-[1.25rem] bg-slate-950 px-5 py-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Pay with wallet
            </button>
            <button
              type="button"
              onClick={() => openPayment('razorpay')}
              disabled={previewLoading || selectedAmountDue <= 0}
              className="rounded-[1.25rem] border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Pay via Razorpay / UPI
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {sortedPlans.map((plan) => {
          const currentById = currentPlanId && currentPlanId === resolvePlanId(plan);
          const currentByName =
            !!status?.hasActiveSubscription &&
            !!status.planName &&
            status.planName.trim().toLowerCase() === plan.name.trim().toLowerCase();
          const isCurrent = Boolean(currentById || currentByName);
          const isPreparing = previewLoading && selectedPlan?.id === resolvePlanId(plan);
          const amount = Number(plan.price || 0);
          const symbol = currencySymbols[plan.currency || 'INR'] || (plan.currency || 'INR');
          const accent = accentMap[plan.planType] || 'from-slate-200 via-slate-100 to-white';

          return (
            <article
              key={resolvePlanId(plan) || plan.name}
              className={`relative overflow-hidden rounded-[2rem] border bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.32)] transition hover:-translate-y-1 hover:shadow-[0_30px_90px_-46px_rgba(15,23,42,0.4)] ${
                isCurrent ? 'border-emerald-300' : 'border-slate-200'
              }`}
            >
              <div className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${accent}`} />
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{plan.planType}</p>
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{plan.name}</h2>
                  </div>
                  {isCurrent ? (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Current
                    </span>
                  ) : (
                    <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                      {plan.billingCycle}
                    </span>
                  )}
                </div>

                <div className="mt-8">
                  <p className="text-4xl font-semibold tracking-tight text-slate-950">
                    {symbol}
                    {amount.toFixed(2)}
                  </p>
                  <p className="mt-2 text-sm text-slate-500">per {plan.billingCycle} cycle</p>
                </div>

                <p className="mt-5 min-h-[72px] text-sm leading-6 text-slate-600">
                  {plan.description || 'Plan details available on selection.'}
                </p>

                <div className="mt-5 space-y-3">
                  {featureList(plan).slice(0, 6).map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={isCurrent || isPreparing}
                  onClick={() => void handleSelectPlan(plan)}
                  className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition ${
                    isCurrent
                      ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                      : 'bg-slate-950 text-white hover:bg-slate-800'
                  }`}
                >
                  {isCurrent ? 'Selected plan' : isPreparing ? 'Calculating...' : 'Select plan'}
                  {!isCurrent ? <ArrowRightIcon className="h-4 w-4" /> : null}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div id="subscription-guarantee" className="rounded-[1.75rem] border border-sky-200 bg-sky-50 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheckIcon className="h-6 w-6 text-sky-700" />
          <div>
            <p className="text-sm font-semibold text-sky-900">Subscription guarantee</p>
            <p className="mt-1 text-sm leading-6 text-sky-800">
              Once subscribed, teacher access stays active for the full billing cycle, listed plan features remain available, and renewal or cancellation state stays visible in subscription status.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherSubscriptionPlans;
